import React from "react";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { Article } from "@domain/lms/article/article";
import { getDisplayedAuthor } from "@domain/lms/utils";
import { useUserPerson } from "@domain/user/hooks";
import { DetailedPerson } from "@domain/user/user";
import { useIsMobile } from "@utils/hooks";
import { TagsView } from "@ui/components/inputs/tagsView";
import { InstructiveButton } from "@ui/components/instructiveButton";
import { UserView } from "@ui/components/profile/userView";
import { gap, mobile } from "@ui/styles/utils";
import { ArticleContentViewer } from "@ui/pages/lms/article/articlePage/content/articleContentViewer";
import { DetailedArticleHeader } from "./detailedArticleHeader";
import { ArticleFeedbacks } from "@ui/pages/lms/article/articlePage/feedback/articleFeedbacks";
import { ArticleSeparator } from "./separator";
import { ArticleSuggestions } from "@ui/pages/lms/article/articlePage/suggestions/articleSuggestions";

interface ArticlePresentationProps extends React.HTMLAttributes<HTMLDivElement> {
	article: Article;
}
export const ArticlePresentation: React.FC<ArticlePresentationProps> = ({ article, ...htmlProps }) => {
	const currentProfile = useCurrentProfile();
	const { articleService } = useServices();
	const isMobile = useIsMobile();
	const author = article.author;
	const { result: authorProfile } = useUserPerson(author?.id || "");
	const isAuthor = author?.id === currentProfile?.id;
	return (
		<Container {...htmlProps}>
			<ArticleContent>
				<DetailedArticleHeader article={article} />
				<ArticleSeparator gapBottom />
				<AuthorWrapper>
					<UserView
						user={getDisplayedAuthor(article)}
						isAuthor={isAuthor}
						followInfos={authorProfile as DetailedPerson}
						showDescription
						realAuthor={article.publisher && article.keepOwnership ? article.author : undefined}
						pictureSize={"L"}
						style={{ flex: 1 }}
					/>
					{!isMobile && (
						<InstructiveButton
							style={{ flex: 1 }}
							content={article}
							isAuthor={isAuthor}
							isAuthenticated={!!currentProfile}
							toggleInstructive={() => articleService.toggleInstructive(article)}
						/>
					)}
				</AuthorWrapper>
				<ArticleSeparator gapBottom />
				<ArticleContentViewer article={article} />
				{isMobile && (
					<StyledInstructiveButtonMobile>
						<InstructiveButton
							style={{ flex: 1 }}
							content={article}
							isAuthor={isAuthor}
							isAuthenticated={!!currentProfile}
							toggleInstructive={() => articleService.toggleInstructive(article)}
						/>
					</StyledInstructiveButtonMobile>
				)}
				<StyledTagsView tags={article.content.tags} withoutTranslation />
				{article.status === "PUBLISHED" && <ArticleSuggestions source={article} />}
				{article.status === "PUBLISHED" && <ArticleFeedbacks article={article} />}
			</ArticleContent>
		</Container>
	);
};

const Container = styled.div`
	width: 80% !important;

	display: grid;
	margin-left: 10%;
	margin-right: 10%;
	margin-top: 20px;
	margin-bottom: 50px;
	grid-template-columns: 1px minmax(0, 1fr);
	grid-template-rows: auto;
	/* grid-column-gap: 20px; */
	grid-row-gap: 24px;
	grid-template-areas:
		"author article"
		". article"
		". article"
		". article";

	@media (max-width: 900px) {
		margin: 10px auto;
		width: 100% !important;
		grid-template-columns: 100%;
		grid-template-areas:
			"article"
			"author";
	}

	> a {
		align-self: flex-end;
		margin-bottom: 10px;
	}
`;

const StyledTagsView = styled(TagsView)`
	justify-content: center;
`;

const AuthorWrapper = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
	margin: 40px 40px;

	@media ${mobile} {
		margin-top: 20px;
	}
`;

const ArticleContent = styled.div`
	grid-area: article;
	${gap(40)};

	@media (max-width: 900px) {
		${gap(15)};
	}
`;

const StyledInstructiveButtonMobile = styled.div`
	display: flex;
	justify-content: center;
	margin-top: 30px;
	margin-bottom: 30px !important;
`;
